import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { Button, Form } from "react-bootstrap";
import { useOutletContext, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import moment from "moment";
import {
  useQuery,
  useInfiniteQuery,
  useQueryClient,
} from "@tanstack/react-query";

import {
  bodyRequest,
  distributorQuick,
  distributorRequest,
} from "../../helpers/utils";
import { endpoints } from "../../helpers/endpoints";

import SkeletonProducts from "./components/SkeletonProducts";
import Filter from "./components/Filter";
import Map from "../../components/Maps";
import Items from "./components/Items";
import Breadcrumb from "./components/Breadcrumb";

import "react-loading-skeleton/dist/skeleton.css";
import "./style.scss";

const Products = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [changeLanguage, language] = useOutletContext();
  const queryClient = useQueryClient();

  const options = [
    {
      value: "Rate-Ascending",
      label: t("rate_ascending"),
    },
    {
      value: "Rate-Descending",
      label: t("rate_descending"),
    },
    {
      value: "Name-Ascending",
      label: t("name_ascending"),
    },
    {
      value: "Name-Descending",
      label: t("name_descending"),
    },
  ];

  // State management
  const [stateMap, setStateMap] = useState([]);
  const [geocodes, setGeocodes] = useState(false);
  const [stateButton, setStateButton] = useState("quick");
  const [selectedOption, setSelectedOption] = useState("");
  const [category, setCategory] = useState();
  const [date, setDate] = useState("");
  const [priceRange, setPriceRange] = useState();
  const [keyword, setKeyword] = useState();
  const [selectedKeyword, setSelectedKeyword] = useState([]);
  const [breadcrumbName, setBreadcrumbName] = useState("");

  const langParams = searchParams.get("lang");

  const obxLang = (lang) => {
    if (lang === "jp") {
      return "ja-JP";
    } else if (lang === "cn" || lang === "tw") {
      return "zh-CN";
    } else {
      return "en-US";
    }
  };

  // Create query parameters object for caching
  const queryParams = useMemo(() => {
    const params = {
      category: searchParams.get("category"),
      min: searchParams.get("min"),
      max: searchParams.get("max"),
      keyword: searchParams.get("keyword"),
      date: searchParams.get("date"),
      sort: searchParams.get("sort"),
      type: searchParams.get("type") || "quick",
      language: obxLang(language),
      selectedKeyword: selectedKeyword,
    };
    return params;
  }, [searchParams, language, selectedKeyword]);

  // Build request body based on current filters
  const buildRequestBody = (params, pageNumber = 1, token = null) => {
    const request = { ...bodyRequest };

    request.request.Paging.PageNumber = pageNumber;
    request.request.Paging.PageSize = 12;
    request.request.Language = params.language;
    request.request.ShortName =
      params.type === "request" ? distributorRequest : distributorQuick;

    if (token) {
      request.request.Paging.Token = token;
    }

    // Set availability
    request.request.Availability = {
      MergeMethod: 1,
      Specific: {
        Date: params.date
          ? moment(params.date).format("YYYY-MM-DD")
          : moment().format("YYYY-MM-DD"),
        Duration: 1,
      },
    };

    // Set filter
    request.request.Filter = {
      TagCriteria: {},
      Type: "Service",
    };

    if (params.category) {
      request.request.Filter.TagCriteria = {
        IndustryCategoryGroups: [params.category],
      };
    }

    if (params.min || params.max) {
      request.request.Filter.Bookability = {
        RateRange: {
          Min: params.min,
          Max: params.max,
        },
      };
    }

    if (params.keyword) {
      request.request.Filter.Names = [`%${params.keyword}%`];
    }

    if (params.selectedKeyword && params.selectedKeyword.length > 0) {
      const keywordNames = params.selectedKeyword.map((item) => `%${item}%`);
      if (params.keyword) {
        keywordNames.push(`%${params.keyword}%`);
      }
      request.request.Filter.Names = keywordNames;
    }

    // Set sorting
    if (params.sort) {
      request.request.Sorting = [
        {
          By: `${params.sort.split("-")[0]}`,
          Direction: `${params.sort.split("-")[1]}`,
        },
      ];
    } else {
      request.request.Sorting = [
        {
          By: "Random",
        },
      ];
    }

    delete request.request.Filter.Ids;

    return request;
  };

  // Fetch products with infinite query for pagination
  const {
    data: productsData,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isLoading,
    error: productsError,
  } = useInfiniteQuery({
    queryKey: ["products", queryParams],
    queryFn: async ({ pageParam = { page: 1, token: null } }) => {
      const requestBody = buildRequestBody(
        queryParams,
        pageParam.page,
        pageParam.token
      );
      const response = await axios.post(endpoints.search, requestBody);

      // Apply client-side sorting for rate-based sorts
      let entities = response.data.Entities;
      if (queryParams.sort === "Rate-Ascending") {
        entities.sort((a, b) => {
          return (
            a.Availability?.Calendar?.LowestRate -
            b.Availability?.Calendar?.LowestRate
          );
        });
      } else if (queryParams.sort === "Rate-Descending") {
        entities.sort((a, b) => {
          return (
            b.Availability?.Calendar?.LowestRate -
            a.Availability?.Calendar?.LowestRate
          );
        });
      }

      return {
        entities,
        paging: response.data.Paging,
        page: pageParam.page,
      };
    },
    getNextPageParam: (lastPage) => {
      if (lastPage.page < lastPage.paging.NumberOfPages) {
        return {
          page: lastPage.page + 1,
          token: lastPage.paging.Token,
        };
      }
      return undefined;
    },
    staleTime: 8 * 60 * 60 * 1000, // 8 hours
    gcTime: 8 * 60 * 60 * 1000, // 8 hours (replaces cacheTime in v5)
    refetchOnMount: false, // Don't refetch on component mount if cache exists
    refetchOnWindowFocus: false, // Don't refetch when window regains focus
    refetchOnReconnect: false, // Don't refetch on network reconnect
    refetchInterval: false, // Disable automatic background refetching
  });

  // Fetch map data only when map view is active
  const { data: mapData } = useQuery({
    queryKey: ["map", queryParams],
    queryFn: async () => {
      const requestBody = buildRequestBody(queryParams);
      requestBody.request.Paging = {};
      const response = await axios.post(endpoints.search, requestBody);
      return response.data.Entities;
    },
    staleTime: 8 * 60 * 60 * 1000, // 8 hours
    gcTime: 8 * 60 * 60 * 1000, // 8 hours (replaces cacheTime in v5)
    enabled: stateButton === "map", // Only fetch when map view is active
    refetchOnMount: false, // Don't refetch on component mount if cache exists
    refetchOnWindowFocus: false, // Don't refetch when window regains focus
    refetchOnReconnect: false, // Don't refetch on network reconnect
    refetchInterval: false, // Disable automatic background refetching
  });

  // Derived data from queries
  const services = useMemo(() => {
    if (!productsData?.pages) return [];
    return productsData.pages.flatMap((page) => page.entities);
  }, [productsData]);

  const pageCount = productsData?.pages?.[0]?.paging?.NumberOfResults || 0;
  const totalPages = productsData?.pages?.[0]?.paging?.NumberOfPages || 1;
  const currentPage = productsData?.pages?.length || 1;

  // Initialize component
  useEffect(() => {
    searchParams.delete("pages");
    setSearchParams(searchParams);

    const category = searchParams.get("category");
    const min = searchParams.get("min");
    const max = searchParams.get("max");
    const keyword = searchParams.get("keyword");
    const date = searchParams.get("date");
    const sort = searchParams.get("sort");

    if (category) {
      setCategory(parseInt(category));
    }

    if (min || max) {
      setPriceRange(`${min}-${max}`);
    }

    if (date) {
      setDate(new Date(date));
    }

    if (keyword) {
      setKeyword(keyword);
    }

    if (sort) {
      setSelectedOption(sort);
    }

    pageNames();

    const idParam = searchParams.get("token");
    if (idParam) {
      sessionStorage.setItem("token", idParam);
    }
  }, []);

  // Handle language changes
  useEffect(() => {
    if (langParams === language) {
      pageNames();
      // Only invalidate if language actually changed
      const currentLang = queryClient.getQueryData(["currentLanguage"]);
      if (currentLang !== language) {
        queryClient.setQueryData(["currentLanguage"], language);
        queryClient.invalidateQueries(["products"]);
        if (stateButton === "map") {
          queryClient.invalidateQueries(["map"]);
        }
      }
      window.scrollTo(0, 0);
    }
  }, [language, queryClient, langParams, stateButton]);

  // Update map data when mapData changes
  useEffect(() => {
    if (mapData) {
      setStateMap(mapData);
      const hasGeocodes = mapData.some((item) => item.HasGeocodes);
      setGeocodes(hasGeocodes);
    }
  }, [mapData]);

  // Filter data function
  const filterData = (values) => {
    // Reset search params
    searchParams.delete("pages");

    if (values.minRange) {
      if (values.minRange === "0") {
        searchParams.delete("min");
        searchParams.delete("max");
      } else {
        searchParams.set("min", values.minRange);
        searchParams.set("max", values.maxRange);
      }
    }

    if (values.date) {
      if (moment(values.date).format("LL") !== moment().format("LL")) {
        searchParams.set(
          "date",
          moment(new Date(values.date)).format("YYYY-MM-DD")
        );
      } else {
        searchParams.delete("date");
      }
      setDate(values.date);
    } else {
      searchParams.delete("date");
      setDate("");
    }

    if (!values.category) {
      searchParams.delete("category");
      setCategory(undefined);
    } else {
      searchParams.set("category", values.category);
      setCategory(values.category);
    }

    if (values.keyword) {
      searchParams.set("keyword", values.keyword);
      setKeyword(values.keyword);
    } else {
      searchParams.delete("keyword");
      setKeyword("");
    }

    setSearchParams(searchParams);
    pageNames();

    // Check if cache exists for new query params before invalidating
    const newQueryParams = {
      ...queryParams,
      category: values.category,
      min: values.minRange !== "0" ? values.minRange : undefined,
      max: values.maxRange !== "0" ? values.maxRange : undefined,
      keyword: values.keyword,
      date: values.date
        ? moment(new Date(values.date)).format("YYYY-MM-DD")
        : undefined,
    };

    // Only invalidate if cache doesn't exist or is stale
    const existingProductsCache = queryClient.getQueryData([
      "products",
      newQueryParams,
    ]);
    const existingMapCache = queryClient.getQueryData(["map", newQueryParams]);

    if (!existingProductsCache) {
      queryClient.invalidateQueries(["products"]);
    }

    if (stateButton === "map" && !existingMapCache) {
      queryClient.invalidateQueries(["map"]);
    }

    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "instant",
    });
  };

  const changeToQuick = () => {
    setStateButton("quick");
    searchParams.set("type", "quick");
    setSearchParams(searchParams);

    // No need to invalidate - let queries handle the state change
  };

  const changeToMap = () => {
    setStateButton("map");
  };

  // Sort function
  const onSort = (value) => {
    setSelectedOption(value);
    searchParams.set("sort", value);
    setSearchParams(searchParams);

    // Check if cache exists for new sort before invalidating
    const newQueryParams = { ...queryParams, sort: value };
    const existingCache = queryClient.getQueryData([
      "products",
      newQueryParams,
    ]);

    if (!existingCache) {
      queryClient.invalidateQueries(["products"]);
    }
  };

  // Load more data
  const loadMore = () => {
    if (hasNextPage && !isFetching) {
      fetchNextPage();
    }
  };

  const pageNames = () => {
    let breadcrumb = "";
    const category = searchParams.get("category");
    switch (category) {
      case "0":
        breadcrumb = t("accommodation");
        break;
      case "1":
        breadcrumb = t("activity");
        break;
      case "2":
        breadcrumb = t("restaurant");
        break;
      case "3":
        breadcrumb = t("shopping");
        break;
      default:
        breadcrumb = t("search");
        break;
    }

    setBreadcrumbName(breadcrumb);
  };

  const getData = ({ loadMore: isLoadMore } = {}) => {
    if (isLoadMore && hasNextPage && !isFetching) {
      fetchNextPage();
    }
    // Remove refetchProducts() as it's handled by query invalidation
  };

  const skeletonShow = isLoading ? "block" : "none";
  const productsShow = !isLoading ? "block" : "none";
  const error = productsError;

  return (
    <div className="products ">
      <div className="productsWrapper" style={{ display: productsShow }}>
        <Breadcrumb category={category} breadcrumbName={breadcrumbName} />
        <div className="text-3xl lg:text-3xl font-bold container text-center mt-16">
          {t("experience_reservation")}
        </div>
        <div className="title_border"></div>
        <Filter
          lang={language}
          filter={filterData}
          date={date}
          category={category}
          priceRange={priceRange}
          keyword={keyword}
          setKeyword={setKeyword}
          setDate={setDate}
          setCategory={setCategory}
          setPriceRange={setPriceRange}
          selectedKeyword={selectedKeyword}
          setSelectedKeyword={setSelectedKeyword}
        />

        <div className="pt-8 pb-32">
          <div className="container max-w-[1000px] flex flex-col lg:flex-row gap-2 lg:justify-between lg:items-center mb-8 options">
            <div className="searchCount">
              <span>{pageCount}</span>
              <span className="count">{t("product_items_jp")}</span>{" "}
              {t("product_items")}
            </div>
            <div className="flex lg:justify-end justify-start items-center flex-wrap lg:!flex-nowrap">
              <Form.Select
                className=" lg:max-w-[160px] flex-shrink-0"
                onChange={(e) => onSort(e.target.value)}
              >
                {options.map((o, i) => (
                  <option
                    key={i}
                    selected={selectedOption === o.value}
                    value={o.value}
                  >
                    {o.label}
                  </option>
                ))}
              </Form.Select>
              <Form.Select
                className="w-full flex-shrink-0 lg:max-w-[190px]"
                onChange={(e) => changeLanguage(e.target.value)}
                value={langParams ? langParams : language}
              >
                <option value="jp">日本語 (Japanese)</option>
                <option value="en">英語 (English)</option>
              </Form.Select>
              <div className="flex items-center">
                <Button
                  variant={stateButton === "quick" ? "primary" : "secondary"}
                  onClick={() => changeToQuick()}
                  className="whitespace-nowrap"
                >
                  {t("quick_booking")}
                </Button>
                <Button
                  variant={stateButton === "map" ? "primary" : "secondary"}
                  onClick={() => changeToMap()}
                  className="whitespace-nowrap"
                >
                  {t("map")}
                </Button>
              </div>
            </div>
          </div>
          <div className="container max-w-[1000px] mx-auto">
            <div
              className="productItems"
              style={{ display: stateButton !== "map" ? "block" : "none" }}
            >
              <Items
                services={services}
                loadMore={loadMore}
                totalPage={totalPages}
                currentPage={currentPage}
                category={category}
                stateButton={stateButton}
                error={error}
                getData={getData}
                hasNextPage={hasNextPage}
                isFetching={isFetching}
              />
            </div>
            <div
              className="productsMap"
              style={{ display: stateButton === "map" ? "block" : "none" }}
            >
              {geocodes && stateMap.length > 0 ? (
                <Map positions={stateMap} zoom={9} />
              ) : (
                <div className="flex justify-center items-center h-[200px] text-xl font-bold">
                  {t("no_data_available")}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="container">
        <div className="skeletonWrapper" style={{ display: skeletonShow }}>
          <SkeletonProducts currentPage={currentPage} category={category} />
        </div>
      </div>
    </div>
  );
};

export default Products;
